import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { 
  MessageCircle, 
  X, 
  ArrowRight, 
  Phone, 
  Mail, 
  Calendar, 
  Send, 
  Paperclip, 
  Image, 
  Smile, 
  ChevronDown, 
  User,
  Mic,
  Camera,
  Sparkles,
  Clock,
  BadgeCheck,
  Heart,
  Star,
  Loader2,
  Check,
  CheckCircle,
  ArrowLeft,
  Headphones,
  Maximize2,
  Minimize2
} from 'lucide-react';

const FloatingInquiryButton: React.FC = () => {
  const { currentStep, setStep } = useCustomizationStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // Start collapsed
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;
    
    const message = {
      id: Date.now(),
      text: newMessage,
      sender: 'user',
      timestamp: new Date(),
      status: 'sending'
    };
    
    setMessages(prev => [...prev, message]);
    setNewMessage('');
    setIsTyping(true);
    
    // Simulate response
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [
        ...prev,
        {
          id: Date.now(),
          text: "Thank you for your message. How can I help you today?",
          sender: 'agent',
          timestamp: new Date(),
          status: 'delivered'
        }
      ]);
    }, 2000);
  };

  const handleVoiceRecord = () => {
    setIsRecording(!isRecording);
  };

  const handleImageUpload = () => {
    setIsCameraOpen(!isCameraOpen);
  };

  const handleSuggestionClick = (suggestion) => {
    setNewMessage(suggestion);
  };

  const getMessageStatusIcon = (status) => {
    switch (status) {
      case 'sending':
        return <Loader2 className="w-3 h-3 animate-spin" />;
      case 'sent':
        return <Check className="w-3 h-3" />;
      case 'delivered':
        return <CheckCircle className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const contactOptions = [
    {
      label: 'Schedule a Call',
      description: 'Book a consultation with our experts',
      icon: Phone,
      action: '#',
      nextSlot: 'Next slot in 2h'
    },
    {
      label: 'Send Email',
      description: 'Get a detailed response to your inquiry',
      icon: Mail,
      action: 'mailto:support@example.com',
      responseTime: '24h response'
    },
    {
      label: 'Book Appointment',
      description: 'Schedule an in-person consultation',
      icon: Calendar,
      action: '#',
      availability: 'Available today'
    }
  ];

  const buttonVariants = {
    expanded: {
      width: 'auto',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    },
    collapsed: {
      width: '48px',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    }
  };

  const chatPanelVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 20, scale: 0.95 }
  };

  const messageVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  if (currentStep === 'inquiry' || currentStep === 'confirmation') {
    return null;
  }

  return (
    <div className="fixed bottom-0 right-0 z-50 p-4 sm:p-6 pb-24 sm:pb-32">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={chatPanelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed sm:absolute bottom-24 sm:bottom-full right-4 left-4 sm:left-auto sm:right-0 sm:mb-4 sm:w-[400px] bg-white rounded-2xl shadow-xl overflow-hidden border border-gold/10"
            style={{
              maxHeight: 'calc(100vh - 180px)',
              height: showChat ? '600px' : 'auto'
            }}
          >
            {showChat ? (
              <div className="flex flex-col h-full">
                <div className="relative p-6 bg-gradient-to-br from-gold to-gold-dark text-white flex-shrink-0">
                  <div className="absolute top-4 right-4 flex items-center gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowChat(false)}
                      className="text-white/80 hover:text-white transition-colors flex items-center gap-1"
                      aria-label="Back to menu"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span className="text-sm">Menu</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setIsOpen(false)}
                      className="text-white/80 hover:text-white transition-colors"
                      aria-label="Close chat"
                    >
                      <X className="w-5 h-5" />
                    </motion.button>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                        <User className="w-6 h-6" />
                      </div>
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">Sarah</h3>
                        <BadgeCheck className="w-4 h-4 text-blue-400" />
                      </div>
                      <p className="text-sm text-white/80">Design Consultant</p>
                    </div>
                    <div className="ml-auto text-right">
                      <div className="flex items-center gap-1 text-xs text-white/80">
                        <Clock className="w-3 h-3" />
                        <span>Avg. response 2m</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-white/80">
                        <Star className="w-3 h-3 fill-current text-yellow-400" />
                        <span>4.9/5 (2.1k reviews)</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map(message => (
                    <motion.div
                      key={message.id}
                      variants={messageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] ${
                          message.sender === 'user'
                            ? 'ml-4'
                            : 'mr-4'
                        }`}
                      >
                        {message.type === 'suggestion' ? (
                          <div className="space-y-2">
                            {message.text && (
                              <div className={`rounded-xl p-3 ${
                                message.sender === 'user'
                                  ? 'bg-gold text-white'
                                  : 'bg-gray-100 text-gray-900'
                              }`}>
                                <p>{message.text}</p>
                              </div>
                            )}
                            <div className="grid grid-cols-1 gap-2">
                              {message.suggestions?.map((suggestion, index) => (
                                <motion.button
                                  key={index}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => handleSuggestionClick(suggestion)}
                                  className="p-2 text-sm bg-gold/10 hover:bg-gold/20 text-gold rounded-lg transition-colors text-left"
                                >
                                  {suggestion}
                                </motion.button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div
                            className={`rounded-xl p-3 ${
                              message.sender === 'user'
                                ? 'bg-gold text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            <p>{message.text}</p>
                            <div className="flex items-center justify-end gap-1 mt-1">
                              <p className="text-xs opacity-70">
                                {message.timestamp.toLocaleTimeString([], { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </p>
                              {message.sender === 'user' && (
                                <span className="text-white/70">
                                  {getMessageStatusIcon(message.status)}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                  {isTyping && (
                    <motion.div
                      variants={messageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      className="flex justify-start"
                    >
                      <div className="bg-gray-100 rounded-xl p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-gold rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handleVoiceRecord}
                      className={`p-2 rounded-lg transition-colors ${
                        isRecording 
                          ? 'bg-red-500 text-white' 
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      aria-label="Record voice message"
                    >
                      <Mic className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handleImageUpload}
                      className={`p-2 rounded-lg transition-colors ${
                        isCameraOpen 
                          ? 'bg-blue-500 text-white' 
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      aria-label="Upload image"
                    >
                      <Camera className="w-5 h-5" />
                    </motion.button>
                    <div className="relative flex-1">
                      <input
                        ref={inputRef}
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        placeholder="Type your message..."
                        className="w-full px-4 py-2 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
                        aria-label="Message input"
                      />
                      <button
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                        aria-label="Add emoji"
                      >
                        <Smile className="w-5 h-5" />
                      </button>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      className="p-2 text-gold hover:text-gold-dark disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Send message"
                    >
                      <Send className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col">
                <div className="relative p-6 bg-gradient-to-br from-gold to-gold-dark text-white">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                    aria-label="Close menu"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <h3 className="text-lg font-semibold mb-2">Need Assistance?</h3>
                  <p className="text-white/90 text-sm">
                    Choose how you'd like to connect with our design experts
                  </p>
                </div>

                <div className="p-4 max-h-[calc(100vh-300px)] overflow-y-auto">
                  <div className="grid gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowChat(true)}
                      className="flex items-center gap-4 p-4 rounded-xl bg-gold text-white hover:bg-gold-dark transition-colors"
                    >
                      <div className="relative p-2 rounded-lg bg-white/20">
                        <MessageCircle className="w-6 h-6" />
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">Live Chat</h4>
                          <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">
                            Online
                          </span>
                        </div>
                        <p className="text-sm text-white/80">Chat with a design expert now</p>
                      </div>
                      <ArrowRight className="w-5 h-5 ml-auto" />
                    </motion.button>

                    {contactOptions.map((option) => (
                      <motion.a
                        key={option.label}
                        href={option.action}
                        onClick={(e) => {
                          if (option.onClick) {
                            e.preventDefault();
                            option.onClick();
                          }
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center gap-4 p-4 rounded-xl bg-cream hover:bg-gold/5 transition-colors group"
                      >
                        <div className="p-2 rounded-lg bg-gold/10 group-hover:bg-gold/20">
                          <option.icon className="w-6 h-6 text-gold" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gold-dark">{option.label}</h4>
                          <p className="text-sm text-black/70">{option.description}</p>
                        </div>
                        <div className="text-right">
                          {'availability' in option && (
                            <span className="text-xs text-green-600 font-medium">
                              {option.availability}
                            </span>
                          )}
                          {'responseTime' in option && (
                            <span className="text-xs text-blue-600 font-medium">
                              {option.responseTime}
                            </span>
                          )}
                          {'nextSlot' in option && (
                            <span className="text-xs text-purple-600 font-medium">
                              {option.nextSlot}
                            </span>
                          )}
                        </div>
                        <ArrowRight className="w-5 h-5 text-gold ml-2" />
                      </motion.a>
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gold/10">
                    <button
                      onClick={() => {
                        setStep('inquiry');
                        setIsOpen(false);
                      }}
                      className="w-full bg-gold text-white py-3 px-4 rounded-xl font-medium hover:bg-gold-dark transition-colors flex items-center justify-center gap-2"
                    >
                      Request Quote Now
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={false}
        animate={isExpanded ? 'expanded' : 'collapsed'}
        variants={buttonVariants}
        className="relative group"
      >
        {/* Glow Effect */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute inset-0 bg-gradient-to-r from-gold/30 to-gold-dark/30 rounded-xl blur-xl"
        />

        {/* Button Content */}
        <motion.button
          onClick={() => {
            if (isExpanded) {
              setIsOpen(!isOpen);
            } else {
              setIsExpanded(true);
            }
          }}
          className="relative bg-gradient-to-r from-gold to-gold-dark rounded-xl shadow-lg flex items-center gap-3 group-hover:shadow-2xl transition-all duration-300 overflow-hidden"
        >
          <div className="flex items-center gap-3 p-3">
            {/* Icon Container */}
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Headphones className="w-6 h-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
            </div>

            {/* Text Content */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="flex-1 min-w-0 pr-3"
                >
                  <div className="hidden sm:block">
                    <p className="text-white font-medium leading-tight">Need Assistance?</p>
                    <p className="text-white/80 text-sm truncate">Chat with our experts</p>
                  </div>
                  <p className="sm:hidden text-white font-medium">Chat Now</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Toggle Expand/Collapse Button */}
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
              if (!isExpanded) {
                setIsOpen(false);
              }
            }}
            className="p-3 hover:bg-white/10 transition-colors"
          >
            {isExpanded ? (
              <Minimize2 className="w-5 h-5 text-white" />
            ) : (
              <Maximize2 className="w-5 h-5 text-white" />
            )}
          </motion.button>
        </motion.button>

        {/* Notification Badge */}
        {unreadCount > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center border-2 border-white"
          >
            <span className="text-xs font-bold text-white">{unreadCount}</span>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default FloatingInquiryButton;